import { ChevronDownIcon, ChevronUpIcon } from '@/components/base/Icons/RocheIconPack';
import { useTableSearchHighlightTerms } from '@/storybook/context/TableSearchHighlightContext';
import { TableOptions } from '@/types/table';
import { Cell, CellContext } from '@tanstack/react-table';
import cx from 'classnames';
import React, { JSX, useCallback } from 'react';
import styles from '../Table.module.scss';
import { ColumnOffsets, getColumnWidthStyle, getStickyColumnProps, PositionedColumnDef } from '../utils/columnLayout';
import { renderSearchAwareContent } from '../utils/renderSearchAwareContent';
import TableCellContent from './TableCellContent';

enum CellType {
  Grouped,
  Aggregated,
  Placeholder,
  Default,
}

type CellRendererProps<T, TValue> = {
  cell: Cell<T, TValue>;
  toggleExpandGroup: () => void;
  onRowExpand?: (row: T) => void;
  options?: TableOptions<T>;
  isRowExpandButtonDisabled?: boolean;
  searchHighlightValue?: string;
  searchTerms: string[];
};

type ExtendedColumnDef<T, TValue> = Cell<T, TValue>['column']['columnDef'] & {
  additionalContentCellClassname?: string;
  sticky?: boolean;
  multiline?: boolean;
  mergedGroup?: string;
  size?: number;
  tooltipPlace?: TableOptions<T>['tooltipPlace'];
  getHighlightValue?: (value: unknown, row: T) => string | number | null | undefined;
};

type CellTemplate<T, TValue> = ExtendedColumnDef<T, TValue>['cell'] | ExtendedColumnDef<T, TValue>['aggregatedCell'];

const renderCellTemplate = <T, TValue>(
  template: CellTemplate<T, TValue>,
  context: CellContext<T, TValue>,
): React.ReactNode => {
  if (template === undefined) {
    return null;
  }

  return typeof template === 'function' ? template(context) : template;
};

const hasGetHighlightValue = <T, TValue>(
  columnDef: Cell<T, TValue>['column']['columnDef'],
): columnDef is Cell<T, TValue>['column']['columnDef'] & {
  getHighlightValue: NonNullable<ExtendedColumnDef<T, TValue>['getHighlightValue']>;
} => 'getHighlightValue' in columnDef && typeof columnDef.getHighlightValue === 'function';

const getSearchAwareColumnDef = <T, TValue>(
  columnDef: Cell<T, TValue>['column']['columnDef'],
): Pick<ExtendedColumnDef<T, TValue>, 'getHighlightValue' | 'multiline'> => ({
  getHighlightValue: hasGetHighlightValue(columnDef) ? columnDef.getHighlightValue : undefined,
  multiline: 'multiline' in columnDef && typeof columnDef.multiline === 'boolean' ? columnDef.multiline : undefined,
});

const renderGroupedCell = <T, TValue>({
  cell,
  toggleExpandGroup,
  options,
  isRowExpandButtonDisabled,
  searchHighlightValue,
  searchTerms,
}: CellRendererProps<T, TValue>): JSX.Element => {
  const row = cell.row;
  const isExpanded = row.getIsExpanded();
  const shouldDisplayExpand = options?.shouldDisplayRowExpand?.(cell.row.original) ?? true;
  const ExpandIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;
  const showExpandHere = !options?.expandIconColumnId;

  const handleExpandClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    toggleExpandGroup?.();
    if (!isExpanded && typeof options?.lazy?.onRowExpand === 'function') options.lazy.onRowExpand(row.original);
  };

  const renderedContent = renderCellTemplate(cell.column.columnDef.cell, cell.getContext());

  return (
    <div className={styles.groupedCellWrapper}>
      <TableCellContent cell={cell} tooltipPlace={options?.tooltipPlace}>
        {renderSearchAwareContent({
          columnDef: getSearchAwareColumnDef(cell.column.columnDef),
          cellValue: cell.getValue(),
          content: renderedContent,
          renderedValue: cell.renderValue(),
          row: cell.row.original,
          searchValue: searchHighlightValue,
          searchTerms,
        })}
      </TableCellContent>
      {showExpandHere && (
        <div
          className={cx(
            styles.groupToggleWrapper,
            'rowExpandIcon',
            isRowExpandButtonDisabled ? styles.disabledRowExpand : '',
            !shouldDisplayExpand ? styles.hideRowExpandIcon : '',
          )}
        >
          {shouldDisplayExpand && <ExpandIcon onClick={handleExpandClick} data-testid={`row-expand-icon-${row.id}`} />}
        </div>
      )}
    </div>
  );
};

const renderAggregatedCell = <T, TValue>({
  cell,
  toggleExpandGroup,
  options,
  isRowExpandButtonDisabled,
  searchHighlightValue,
  searchTerms,
}: CellRendererProps<T, TValue>): JSX.Element => {
  const row = cell.row;
  const isExpanded = row.getIsExpanded();
  const shouldDisplayExpand = options?.shouldDisplayRowExpand?.(cell.row.original) ?? true;
  const showExpandHere = options?.expandIconColumnId === cell.column.id;
  const ExpandIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

  const handleExpandClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    toggleExpandGroup?.();
    if (!isExpanded && typeof options?.lazy?.onRowExpand === 'function') options.lazy.onRowExpand(row.original);
  };

  const renderedContent = renderCellTemplate(
    cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
    cell.getContext(),
  );

  return (
    <div className={showExpandHere ? styles.groupedCellWrapper : undefined}>
      <TableCellContent cell={cell} tooltipPlace={options?.tooltipPlace}>
        {renderSearchAwareContent({
          columnDef: getSearchAwareColumnDef(cell.column.columnDef),
          cellValue: cell.getValue(),
          content: renderedContent,
          renderedValue: cell.renderValue(),
          row: cell.row.original,
          searchValue: searchHighlightValue,
          searchTerms,
        })}
      </TableCellContent>
      {showExpandHere && (
        <div
          className={cx(
            styles.groupToggleWrapper,
            'rowExpandIcon',
            isRowExpandButtonDisabled ? styles.disabledRowExpand : '',
            !shouldDisplayExpand ? styles.hideRowExpandIcon : '',
          )}
        >
          {shouldDisplayExpand && <ExpandIcon onClick={handleExpandClick} data-testid={`row-expand-icon-${row.id}`} />}
        </div>
      )}
    </div>
  );
};

const renderPlaceholderCell = (): JSX.Element | null => {
  return null;
};

const renderDefaultCell = <T, TValue>({
  cell,
  options,
  searchHighlightValue,
  searchTerms,
}: CellRendererProps<T, TValue>): JSX.Element => {
  const renderedContent = renderCellTemplate(cell.column.columnDef.cell, cell.getContext());

  return (
    <TableCellContent cell={cell} tooltipPlace={options?.tooltipPlace}>
      {renderSearchAwareContent({
        columnDef: getSearchAwareColumnDef(cell.column.columnDef),
        cellValue: cell.getValue(),
        content: renderedContent,
        renderedValue: cell.renderValue(),
        row: cell.row.original,
        searchValue: searchHighlightValue,
        searchTerms,
      })}
    </TableCellContent>
  );
};

const cellRenderers = {
  [CellType.Grouped]: renderGroupedCell,
  [CellType.Aggregated]: renderAggregatedCell,
  [CellType.Placeholder]: renderPlaceholderCell,
  [CellType.Default]: renderDefaultCell,
};

const getCellType = <T, TValue>({
  cell,
  options,
}: Pick<CellRendererProps<T, TValue>, 'cell' | 'options'>): CellType => {
  if (cell.getIsGrouped()) return CellType.Grouped;
  if (cell.getIsAggregated()) return CellType.Aggregated;
  if (cell.getIsPlaceholder() && !options?.displayExpandedCellValues) return CellType.Placeholder;
  return CellType.Default;
};

type TableCellProps<T, TValue> = {
  cell: Cell<T, TValue>;
  cellIndex: number;
  options?: TableOptions<T>;
  isRowExpandButtonDisabled?: boolean;
  positionedColumnDefs: PositionedColumnDef<T>[];
  columnOffsets: ColumnOffsets;
  searchHighlightValue?: string;
  cornerCellWidth?: number;
};

const TableCell = <T, TValue>({
  cell,
  cellIndex,
  options,
  isRowExpandButtonDisabled,
  positionedColumnDefs,
  columnOffsets,
  searchHighlightValue,
  cornerCellWidth,
}: TableCellProps<T, TValue>) => {
  const row = cell.row;
  const searchTerms = useTableSearchHighlightTerms();
  const currentColumnDef = positionedColumnDefs[cellIndex];
  const rawWidthStyle = getColumnWidthStyle(currentColumnDef?.size);

  const { isStickyColumnLeft, isStickyColumnRight, isLastStickyColumn, isFirstStickyRightColumn, left, right } =
    getStickyColumnProps(positionedColumnDefs, columnOffsets, cellIndex);
  const isMultiline = !!currentColumnDef?.multiline;

  const cellClassName = cx({
    [styles.sticky]: isStickyColumnLeft,
    [styles.stickyRight]: isStickyColumnRight,
    [styles.groupHeader]: row.getCanExpand() ?? false,
    [styles.rowToggler]: cell.getIsGrouped() ?? false,
    [styles.open]: row.getIsExpanded() ?? false,
    [styles.lastStickyColumn]: isLastStickyColumn,
    [styles.firstStickyRightColumn]: isFirstStickyRightColumn,
    [styles.multiline]: isMultiline,
  });

  const toggleExpandGroup = useCallback(() => {
    row.getToggleExpandedHandler()();
    row.pin(!row.getIsExpanded() ? 'top' : false, true);
  }, [row]);

  const cellType = getCellType({ cell, options });
  const CellRenderer = cellRenderers[cellType];

  const calculateColSpan = () => {
    if (options?.verticalColumn && cellIndex === 0) {
      return options?.verticalColumn.numberOfColspan - (row.index <= options?.verticalColumn.rowsStretched ? 1 : 0);
    }
    return 1;
  };

  const verticalColumn = options?.verticalColumn;
  const isVerticalColumnFirstCell = !!verticalColumn && cellIndex === 0;
  const hasCornerCell = !!verticalColumn && isVerticalColumnFirstCell && row.index <= verticalColumn.rowsStretched;

  const widthStyle = hasCornerCell ? undefined : rawWidthStyle;

  let effectiveLeft = left;
  if (isVerticalColumnFirstCell) {
    effectiveLeft = hasCornerCell && cornerCellWidth != null ? `${Math.ceil(cornerCellWidth)}px` : '0';
  }

  return (
    <td
      className={cellClassName}
      style={{ width: widthStyle, minWidth: widthStyle, maxWidth: widthStyle, left: effectiveLeft, right }}
      colSpan={calculateColSpan()}
    >
      <CellRenderer
        cell={cell}
        toggleExpandGroup={toggleExpandGroup}
        options={options}
        isRowExpandButtonDisabled={isRowExpandButtonDisabled}
        searchHighlightValue={searchHighlightValue}
        searchTerms={searchTerms}
      />
    </td>
  );
};

export default TableCell;
